import type { WindowDescriptor } from '@krp/client-runtime'

import type { OneCUiState } from '../../state'
import {
  capturePricing,
  decodePricingSnapshot,
  priceListIntentOf,
  priceListPageChanged,
  priceListTypeChosen,
  restored,
} from './model/pricingSlice'

/** Окна цен и остатков 1С (UB1b). Каждый адрес содержит `:rid` (D-064). */
export const ONEC_PRICING_WINDOWS = {
  overview: 'onec.pricing',
  /** RP-FB10/FB39: цены товаров одного вида (адрес с ключом вида) или всех видов. */
  priceList: 'onec.price-list',
  /** RP-FB39: виды цен базы — вкладка «Цены» раздела. */
  priceTypes: 'onec.price-types',
} as const

const text = (value: unknown, max: number) => (typeof value === 'string' ? value.slice(0, max) : '')

export const pricingWindows: readonly WindowDescriptor<OneCUiState>[] = [
  {
    kind: ONEC_PRICING_WINDOWS.priceTypes,
    scope: 'resource',
    paths: ['/resources/:rid/price-types'],
    label: 'Виды цен 1С',
  },
  {
    kind: ONEC_PRICING_WINDOWS.priceList,
    scope: 'resource',
    // `:typeKey` — цены одного вида (из списка видов цен); без него — все виды одним списком.
    paths: ['/resources/:rid/price-list/:typeKey', '/resources/:rid/price-list'],
    label: 'Цены 1С',
    // Вкладка помнит выбор человека — вид цен и страницу, — а не цены: они перечитываются. Поиска в списке цен нет.
    capture: (state, params) => {
      const intent = priceListIntentOf(state.pricing, params.rid ?? '')
      return { priceType: intent.priceType, page: intent.page }
    },
    restore: (snapshot, params) => {
      if (params.rid === undefined) return []
      const resourceId = params.rid
      const priceType = text(snapshot.priceType, 64)
      const page = typeof snapshot.page === 'number' && Number.isInteger(snapshot.page) && snapshot.page >= 0 ? snapshot.page : 0
      return [
        ...(priceType === '' ? [] : [priceListTypeChosen({ resourceId, priceType })]),
        priceListPageChanged({ resourceId, page: Math.min(page, 100_000) }),
      ]
    },
  },
  {
    kind: ONEC_PRICING_WINDOWS.overview,
    scope: 'resource',
    paths: ['/resources/:rid/pricing'],
    label: 'Цены и остатки',
    size: 'lg',
    // Вкладка помнит набранную пару и список ключей этой базы, а не ответы 1С.
    capture: (state, params) => ({ ...capturePricing(state.pricing, params.rid ?? '') }),
    restore: (snapshot, params) => {
      const decoded = decodePricingSnapshot(snapshot)
      return decoded === undefined || params.rid === undefined ? [] : [restored({ resourceId: params.rid, snapshot: decoded })]
    },
  },
]
